import { useCallback, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Table, Text, VStack } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import { AIChatbotFAQFragment } from 'types/graphql'

import { useSearchAIChatbotFAQsQuery } from 'src/fetch/aiChatbotFAQ'
import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import AskedCountCell from 'src/pages/AIAssistantTrainPage/AskedCountCell'
import UnansweredQuestionsTableButtons from 'src/pages/AIAssistantTrainPage/UnansweredQuestionsTableButtons'

type QuestionCellProps = {
  question: string
}

const QuestionCell = ({ question }: QuestionCellProps) => {
  return (
    <VStack gap={1} align="start">
      <Text.Body className="line-clamp-3">{question}</Text.Body>
    </VStack>
  )
}

const UnansweredQuestionsTable = () => {
  const organizationId = useMyOrgId()
  const getRowId = useCallback((faq: AIChatbotFAQFragment) => `${faq.id}`, [])
  const { debouncedSearchQuery, ...tableProps } = useTableState<AIChatbotFAQFragment>({
    initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
  })
  const { count, faqs, isFetching } = useSearchAIChatbotFAQsQuery({
    organizationId,
    pagination: {
      page: tableProps.pagination.pageIndex + 1,
      pageSize: tableProps.pagination.pageSize,
      query: debouncedSearchQuery,
    },
    type: 'Unanswered',
  })

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<AIChatbotFAQFragment>()
    return [
      columnHelper.accessor((faq) => faq.count, {
        id: 'asked',
        header: '# Times Asked',
        cell: (info) => <AskedCountCell count={info.getValue()} />,
        enableSorting: false,
        meta: {
          width: '120px',
          textAlign: 'center',
        },
      }),
      columnHelper.accessor((faq) => faq.question, {
        id: 'question',
        header: 'Question',
        cell: (info) => <QuestionCell question={info.getValue() ?? ''} />,
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        meta: { width: '280px' },
        cell: (info) => <UnansweredQuestionsTableButtons aiChatbotFAQ={info.row.original} />,
      }),
    ]
  }, [])

  return (
    <Table
      subtitle="Questions from users that don't have custom answers yet. Sorted by most asked."
      columns={columns}
      data={faqs}
      enablePagination
      enableSearching
      searchInputPlaceholder="Search questions..."
      getRowId={getRowId}
      isLoading={isFetching}
      totalCount={count}
      showCount
      emptyStateMessage="You're all caught up! As users interact with the Community Assistant and ask questions, they will appear here for you to answer."
      {...tableProps}
    />
  )
}

export default React.memo(UnansweredQuestionsTable)
